/**
 * ContingencyDeadlineField — the "within N days" deadline of a contingency
 * (`<key>_days`, whole days after acceptance, 1–365).
 *
 * There is no default: a deadline is a negotiated term, not law (legal review
 * §5, "Deadlines"). On every Wisconsin offer (vacant land, residential and
 * condo) it is REQUIRED when the contingency is selected (template 7(a);
 * legal review §9.7; user decision for home and condo), and the server
 * refuses a blank one. Only outside Wisconsin is it optional.
 *
 * Props:
 *   - contingencyKey: e.g. 'soil_evaluation'
 *   - label: the contingency's label, for the accessible name
 *   - value: the current text (or number)
 *   - onChange(text)
 *   - disabled
 *   - required: the option's `deadlineRequired` (default true)
 *   - serverError: the server's message for `<key>_days`, from a 422
 */
import React, { useId } from 'react';
import {
  DEADLINE_INVALID_MESSAGE,
  DEADLINE_INVALID_OPTIONAL_MESSAGE,
  DEADLINE_REQUIRED_MESSAGE,
  MAX_DEADLINE_DAYS,
  MIN_DEADLINE_DAYS,
  deadlineKey,
  parseDeadlineDays,
} from './contingencyCatalog';
import styles from './ContingencyDeadlineField.module.css';

export default function ContingencyDeadlineField({
  contingencyKey,
  label,
  value,
  onChange,
  disabled = false,
  required = true,
  serverError = null,
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const serverErrorId = `${id}-server-error`;
  const text = value === undefined || value === null ? '' : String(value);
  const days = parseDeadlineDays(text);
  const invalid = days === undefined;
  const missing = required && days === null;
  const describedBy = [hintId, invalid && errorId, serverError && serverErrorId].filter(Boolean).join(' ');

  return (
    <div
      className={styles.field}
      data-testid={`contingency-deadline-${contingencyKey}`}
      data-missing={missing ? 'true' : undefined}
    >
      <label htmlFor={id} className={styles.label}>
        Deadline for {label}
        {required && <span className={styles.required} aria-hidden="true"> *</span>}
      </label>
      <div className={styles.row}>
        <input
          id={id}
          className={styles.input}
          type="number"
          inputMode="numeric"
          min={MIN_DEADLINE_DAYS}
          max={MAX_DEADLINE_DAYS}
          step="1"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={required}
          placeholder={required ? 'Days' : 'Optional'}
          aria-describedby={describedBy}
          aria-invalid={invalid || Boolean(serverError) || undefined}
        />
        <span className={styles.unit}>days after acceptance</span>
      </div>
      <p id={hintId} className={`${styles.hint} ${missing ? styles.hintMissing : ''}`}>
        {required
          ? `Required. ${DEADLINE_REQUIRED_MESSAGE}`
          : 'Optional. Leave blank to leave the deadline open on the offer.'}
      </p>
      {invalid && (
        <p id={errorId} className={styles.error} role="alert">
          {required ? DEADLINE_INVALID_MESSAGE : DEADLINE_INVALID_OPTIONAL_MESSAGE}
        </p>
      )}
      {serverError && (
        <p
          id={serverErrorId}
          className={styles.error}
          role="alert"
          data-testid={`contingency-error-${deadlineKey(contingencyKey)}`}
        >
          {serverError}
        </p>
      )}
    </div>
  );
}
